import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { 
  Calendar, 
  MessageCircle, 
  Star, 
  Lock, 
  Eye, 
  Edit3, 
  AlertTriangle,
  Users,
  Clock,
  CalendarDays
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import FeedbackColloquioModal from "./FeedbackColloquioModal";
import ChatModal from "./ChatModal";
import RescheduleColloquioModal from "./RescheduleColloquioModal";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const ColloquiList = ({ userType = 'agenzia' }) => {
  const [colloqui, setColloqui] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedColloquio, setSelectedColloquio] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedRichiestaId, setSelectedRichiestaId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [colloquioToReschedule, setColloquioToReschedule] = useState(null);

  useEffect(() => {
    // Carica userId
    const storedUserId = localStorage.getItem('user_id');
    if (storedUserId) {
      setUserId(parseInt(storedUserId));
    }
    
    // Evita chiamate multiple
    if (!hasLoaded) {
      loadColloqui();
      setHasLoaded(true);
    }
  }, [userType, hasLoaded]);

  const loadColloqui = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Usa endpoint specifici per tipo utente
      let endpoint;
      if (userType === 'agenzia') {
        endpoint = '/api/v1/agenzie/colloqui';
      } else if (userType === 'candidato') {
        endpoint = '/api/v1/candidato/colloqui';
      } else {
        // Fallback per admin
        endpoint = '/api/v1/admin/colloqui/feedback';
      }
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // L'API restituisce direttamente l'array, non dentro un campo 'colloqui'
      const colloquiData = Array.isArray(response.data) ? response.data : (response.data.colloqui || []);
      setColloqui(colloquiData);
    } catch (error) {
      console.error("Errore caricamento colloqui:", error);
      if (error.response?.status === 403) {
        toast.error("Accesso non autorizzato. Effettua il login per visualizzare i colloqui.");
      } else if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
      } else {
        toast.error("Errore nel caricamento dei colloqui");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (stato) => {
    const colors = {
      'CONTATTATO': 'bg-blue-100 text-blue-800',
      'PRIMO_COLLOQUIO': 'bg-yellow-100 text-yellow-800', 
      'SECONDO_COLLOQUIO': 'bg-orange-100 text-orange-800',
      'INSERITO': 'bg-green-100 text-green-800',
      'SCARTATO': 'bg-red-100 text-red-800'
    };
    return colors[stato] || 'bg-gray-100 text-gray-800';
  };

  const canLeaveFeedback = (colloquio) => {
    const validStates = ['PRIMO_COLLOQUIO', 'SECONDO_COLLOQUIO', 'INSERITO', 'SCARTATO'];
    return validStates.includes(colloquio.stato);
  };

  const hasFeedback = (colloquio, type) => {
    return type === 'agenzia' 
      ? colloquio.feedback_agenzia?.feedback 
      : colloquio.feedback_candidato?.feedback;
  };

  const isFeedbackLocked = (colloquio, type) => {
    return type === 'agenzia' 
      ? colloquio.feedback_agenzia?.locked 
      : colloquio.feedback_candidato?.locked;
  };

  const openFeedbackModal = (colloquio) => {
    setSelectedColloquio(colloquio);
    setShowFeedbackModal(true);
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400">Non valutato</span>;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-sm text-gray-600 ml-1">({rating}/5)</span>
      </div>
    );
  };

  const renderFeedbackStatus = (colloquio, type) => {
    const feedback = type === 'agenzia' ? colloquio.feedback_agenzia : colloquio.feedback_candidato;
    const locked = isFeedbackLocked(colloquio, type);
    const has = hasFeedback(colloquio, type);

    if (!canLeaveFeedback(colloquio)) {
      return (
        <span className="text-xs text-gray-500 flex items-center">
          <Clock className="w-3 h-3 mr-1" />
          In attesa colloquio
        </span>
      );
    }

    if (has && locked) {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-xs text-green-600 flex items-center">
            <Lock className="w-3 h-3 mr-1" />
            Feedback inviato
          </span>
          {feedback?.data && (
            <span className="text-xs text-gray-500">
              {new Date(feedback.data).toLocaleDateString('it-IT')}
            </span>
          )}
        </div>
      );
    }

    return (
      <span className="text-xs text-orange-600 flex items-center">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Feedback mancante
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Caricamento colloqui...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestione Colloqui</h2>
          <p className="text-gray-600">
            {colloqui.length} colloqui trovati
          </p>
        </div>
        <Button onClick={() => {
          setHasLoaded(false);
          loadColloqui();
        }} variant="outline">
          Aggiorna
        </Button>
      </div>

      {/* Lista colloqui */}
      {colloqui.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nessun colloquio trovato
          </h3>
          <p className="text-gray-600">
            Non ci sono ancora colloqui da visualizzare.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {colloqui.map((colloquio) => (
            <div
              key={colloquio.colloquio_id}
              className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              {/* Header colloquio */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {colloquio.agenzia?.nome || 'Agenzia'}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(colloquio.stato)}`}>
                      {colloquio.stato}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600 space-x-4">
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Richiesta: {new Date(colloquio.data_richiesta).toLocaleDateString('it-IT', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })}
                    </span>
                    {colloquio.data_colloquio_proposta && (
                      <span className="flex items-center text-blue-600 font-medium">
                        <Calendar className="w-4 h-4 mr-1" />
                        Colloquio: {new Date(colloquio.data_colloquio_proposta).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Info partecipanti */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Candidato</h4>
                  <p className="text-sm text-blue-800">
                    {colloquio.candidato?.nome} {colloquio.candidato?.cognome}
                  </p>
                  <p className="text-xs text-blue-600">
                    {colloquio.candidato?.email}
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Agenzia</h4>
                  <p className="text-sm text-green-800">
                    {colloquio.agenzia?.nome}
                  </p>
                  <p className="text-xs text-green-600">
                    {colloquio.agenzia?.citta}
                  </p>
                </div>
              </div>

              {/* Feedback section */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Feedback</h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Feedback Agenzia */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        Feedback Agenzia
                      </span>
                      {renderFeedbackStatus(colloquio, 'agenzia')}
                    </div>
                    
                    {colloquio.feedback_agenzia ? (
                      <div className="space-y-2">
                        {renderStars(colloquio.feedback_agenzia.valutazione)}
                        {colloquio.feedback_agenzia.feedback && (
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {colloquio.feedback_agenzia.feedback}
                          </p>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openFeedbackModal(colloquio)}
                          className="w-full mt-2"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Visualizza
                        </Button>
                      </div>
                    ) : (
                      canLeaveFeedback(colloquio) && (
                        <Button 
                          size="sm"
                          onClick={() => openFeedbackModal(colloquio)}
                          className="w-full"
                          disabled={userType !== 'agenzia'}
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Lascia Feedback
                        </Button>
                      )
                    )}
                  </div>

                  {/* Feedback Candidato */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        Feedback Candidato
                      </span>
                      {renderFeedbackStatus(colloquio, 'candidato')}
                    </div>
                    
                    {colloquio.feedback_candidato ? (
                      <div className="space-y-2">
                        {renderStars(colloquio.feedback_candidato.valutazione)}
                        {colloquio.feedback_candidato.feedback && (
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {colloquio.feedback_candidato.feedback}
                          </p>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openFeedbackModal(colloquio)}
                          className="w-full mt-2"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Visualizza
                        </Button>
                      </div>
                    ) : (
                      canLeaveFeedback(colloquio) && (
                        <Button 
                          size="sm"
                          onClick={() => openFeedbackModal(colloquio)}
                          className="w-full"
                          disabled={userType !== 'candidato'}
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Lascia Feedback
                        </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Pulsante Chat e Sposta */}
                <div className="mt-4 space-y-2">
                  {/* Pulsante Chat */}
                  <Button
                    onClick={() => {
                      setSelectedRichiestaId(colloquio.colloquio_id);
                      setChatOpen(true);
                    }}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Apri Chat
                  </Button>

                  {/* Pulsante Lascia Feedback (solo per candidato) */}
                  {userType === 'candidato' && (
                    <Button
                      onClick={() => {
                        setSelectedColloquio(colloquio);
                        setShowFeedbackModal(true);
                      }}
                      className={
                        colloquio.feedback_candidato_data
                          ? "w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white flex items-center justify-center gap-2"
                          : "w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white flex items-center justify-center gap-2"
                      }
                    >
                      <Star className="w-4 h-4" />
                      {colloquio.feedback_candidato_data ? 'Feedback Inviato' : 'Lascia Feedback'}
                    </Button>
                  )}

                  {/* Pulsante Sposta Colloquio (solo per agenzia) */}
                  {userType === 'agenzia' && (
                    <Button
                      onClick={() => {
                        setColloquioToReschedule(colloquio);
                        setShowRescheduleModal(true);
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white flex items-center justify-center gap-2"
                    >
                      <CalendarDays className="w-4 h-4" />
                      Sposta Colloquio
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Feedback */}
      {showFeedbackModal && selectedColloquio && (
        <FeedbackColloquioModal
          isOpen={showFeedbackModal}
          onClose={(success) => {
            setShowFeedbackModal(false);
            
            // Se il feedback è stato inviato con successo, aggiorna il colloquio localmente
            if (success && selectedColloquio) {
              setColloqui(prevColloqui => 
                prevColloqui.map(c => 
                  c.colloquio_id === selectedColloquio.colloquio_id
                    ? { ...c, feedback_candidato_data: new Date().toISOString() }
                    : c
                )
              );
            }
            
            setSelectedColloquio(null);
            setHasLoaded(false); // Force reload
            loadColloqui(); // Ricarica la lista dopo aver chiuso il modal
          }}
          colloquioId={selectedColloquio.colloquio_id}
          userType={userType}
          colloquioData={selectedColloquio}
        />
      )}
      
      {/* Chat Modal */}
      <ChatModal
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setSelectedRichiestaId(null);
        }}
        richiestaId={selectedRichiestaId}
        userType={userType}
        userId={userId}
      />

      {/* Reschedule Modal */}
      <RescheduleColloquioModal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setColloquioToReschedule(null);
        }}
        colloquio={colloquioToReschedule}
        onSuccess={() => {
          setHasLoaded(false);
          loadColloqui();
          toast.success('Colloquio spostato con successo!');
        }}
      />
    </div>
  );
};

export default ColloquiList;